const hasValue = (value) => value !== null && value !== undefined && value !== "";
const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

export const normalizeSaleMoney = (value) => {
  if (!hasValue(value)) return value;
  if (isRecord(value) && hasValue(value.amount_minor)) {
    const amountMinor = Number(value.amount_minor);
    if (Number.isFinite(amountMinor)) return (amountMinor / 100).toFixed(2);
    return "";
  }
  if (typeof value === "number" && !Number.isFinite(value)) return "";
  if (typeof value === "string" && (!value.trim() || !Number.isFinite(Number(value)))) return "";
  return value;
};

export const formatSaleAmount = (value) => {
  const normalized = normalizeSaleMoney(value);
  if (normalized === null || normalized === undefined || normalized === "" || typeof normalized === "object") {
    return "";
  }
  if (typeof normalized !== "number" && typeof normalized !== "string") return "";
  if (!Number.isFinite(Number(normalized))) return "";
  return String(normalized);
};

export const normalizeSaleProduct = (product = {}) => {
  if (!isRecord(product)) return product;
  const retailPrice = normalizeSaleMoney(product.retail_price);
  const costPrice = hasValue(product.cost_price)
    ? normalizeSaleMoney(product.cost_price)
    : retailPrice;
  return {
    ...product,
    cost_price: costPrice,
    retail_price: retailPrice,
    image_url: product.image_url || product.image || null,
  };
};

export const normalizeSaleProductPage = (payload = {}) => ({
  ...payload,
  data: Array.isArray(payload.data) ? payload.data.map(normalizeSaleProduct) : [],
});

const normalizeSaleCollection = (value, normalizeItem) => (
  Array.isArray(value) ? value.map(normalizeItem) : value
);

export const normalizeSaleVoucher = (voucher = {}) => {
  if (!isRecord(voucher)) return voucher;

  const amount = hasValue(voucher.amount) ? normalizeSaleMoney(voucher.amount) : voucher.amount;
  const cost = hasValue(voucher.cost) ? normalizeSaleMoney(voucher.cost) : "";
  const hasCost = cost !== null && cost !== undefined && cost !== "";
  const normalized = {
    ...voucher,
    ...(hasValue(voucher.amount) ? { amount } : {}),
    ...(hasCost || amount !== "" ? { cost: hasCost ? cost : amount } : {}),
    ...(hasValue(voucher.remaining_balance)
      ? { remaining_balance: normalizeSaleMoney(voucher.remaining_balance) }
      : {}),
    ...(Object.prototype.hasOwnProperty.call(voucher, "voucher_to")
      ? { voucher_to: isRecord(voucher.voucher_to) ? voucher.voucher_to : {} }
      : {}),
  };

  return normalized;
};

export const normalizeSaleOneOffVoucher = (voucher = {}) => {
  if (!isRecord(voucher)) return voucher;
  return {
    ...voucher,
    ...(hasValue(voucher.amount) ? { amount: normalizeSaleMoney(voucher.amount) } : {}),
  };
};

export const normalizeSaleMembership = (membership = {}) => {
  if (!isRecord(membership)) return membership;
  return {
    ...membership,
    ...(hasValue(membership.cost) ? { cost: normalizeSaleMoney(membership.cost) } : {}),
    ...(hasValue(membership.credit) ? { credit: normalizeSaleMoney(membership.credit) } : {}),
  };
};

export const normalizeSaleSubscription = (subscription = {}) => {
  if (!isRecord(subscription)) return subscription;
  // The catalog carries currency inside Money DTOs. Preserve it before
  // flattening amounts; explicit legacy root currency remains authoritative.
  const hasRootCurrency = hasValue(subscription.currency) && String(subscription.currency).trim() !== "";
  const moneyCurrencies = [subscription.amount, subscription.one_off_amount]
    .filter((value) => isRecord(value) && hasValue(value.currency) && String(value.currency).trim() !== "")
    .map((value) => String(value.currency).trim().toUpperCase());
  const currency = moneyCurrencies.length > 0
    && moneyCurrencies.every((value) => value === moneyCurrencies[0])
    && /^[A-Z]{3}$/.test(moneyCurrencies[0]) ? moneyCurrencies[0] : "";
  return {
    ...subscription,
    ...(!hasRootCurrency && moneyCurrencies.length > 0 ? { currency } : {}),
    ...(hasValue(subscription.amount) ? { amount: normalizeSaleMoney(subscription.amount) } : {}),
    ...(hasValue(subscription.one_off_amount)
      ? { one_off_amount: normalizeSaleMoney(subscription.one_off_amount) }
      : {}),
  };
};

export const normalizeSaleCart = (cart = {}) => {
  if (!isRecord(cart)) return cart;
  return {
    ...cart,
    products: normalizeSaleCollection(cart.products, normalizeSaleProduct),
    vouchers: normalizeSaleCollection(cart.vouchers, normalizeSaleVoucher),
    oneoffvoucher: normalizeSaleCollection(cart.oneoffvoucher, normalizeSaleOneOffVoucher),
    membership: normalizeSaleCollection(cart.membership, normalizeSaleMembership),
    subscription: normalizeSaleCollection(cart.subscription, normalizeSaleSubscription),
  };
};
